/**
 * Profile > Subscription & billing: the user's payments and invoices
 * (same contract as the EPC app's services/payments/billing.ts).
 *
 *   GET /api/payments/history?page&per_page -- every payment, paginated
 *   GET /api/payments/invoices              -- invoice history
 *   GET /api/payments/invoices/{id}/download -- the invoice PDF (needs the
 *                                              bearer token, so it is fetched
 *                                              as a blob, never a plain link)
 *
 * Swagger has no response schema for these, so the readers accept the
 * snake_case and camelCase spellings the payment API uses elsewhere and leave
 * what they can't find empty rather than guessing.
 */
import { baseURL, requestUrl } from "../../Utils/baseUrls";
import { BillingBasis, FeatureTy, getFeatureByName, getPurchasedCredits } from "./payu";
import { maxPurchasedProjects, PROJECT_CREATION_FEATURE, projectCreateLimit } from "../../Utils/Const";

type Raw = Record<string, unknown>;

const listOf = (data: unknown): Raw[] => {
  if (Array.isArray(data)) return data as Raw[];
  const inner = (data as { responseData?: unknown } | null)?.responseData;
  return Array.isArray(inner) ? (inner as Raw[]) : [];
};

/** The first of `keys` with a value. */
const pick = (row: Raw, keys: string[]): unknown => {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};
const text = (value: unknown): string | null => (value === undefined || value === null || value === "" ? null : String(value));
const num = (value: unknown): number | null => {
  const n = Number(value);
  return value === undefined || value === null || value === "" || !Number.isFinite(n) ? null : n;
};

export const BILLING_BASIS_LABEL: Record<number, string> = {
  [BillingBasis.Monthly]: "Monthly",
  [BillingBasis.Yearly]: "Yearly",
  [BillingBasis.UsageCount]: "Pay per use",
  [BillingBasis.OneTimeProject]: "One-time (project)",
};

const basisLabel = (value: unknown): string | null => {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  if (Number.isFinite(n)) return BILLING_BASIS_LABEL[n] ?? null;
  const byName: Record<string, string> = { monthly: "Monthly", yearly: "Yearly", usagecount: "Pay per use", onetimeproject: "One-time (project)" };
  return byName[String(value).replace(/[^a-z]/gi, "").toLowerCase()] ?? String(value);
};

export interface InvoiceRow {
  id: string;
  number: string | null;
  date: string | null;
  description: string | null;
  amount: number | null;
  currency: string | null;
  status: string | null;
}

export const readInvoice = (row: Raw, index: number): InvoiceRow => ({
  id: text(pick(row, ["invoice_id", "invoiceId", "id"])) ?? `invoice-${index}`,
  number: text(pick(row, ["invoice_number", "invoiceNumber", "invoice_no", "invoiceNo", "number"])),
  date: text(pick(row, ["invoice_date", "invoiceDate", "created_at", "createdAt", "issued_at", "issuedAt", "date"])),
  description: text(pick(row, ["description", "package_name", "packageName", "feature_name", "featureName", "item"])),
  amount: num(pick(row, ["final_amount", "finalAmount", "total_amount", "totalAmount", "amount", "total"])),
  currency: text(pick(row, ["currency", "currency_code", "currencyCode"])),
  status: text(pick(row, ["status", "payment_status", "paymentStatus", "invoice_status", "invoiceStatus"])),
});

/** Whether the row came with the backend's invoice id (the download needs it). */
export const hasInvoiceId = (row: InvoiceRow): boolean => !row.id.startsWith("invoice-");

export const getMyInvoices = async (): Promise<InvoiceRow[]> => {
  const { data } = await baseURL.get(requestUrl.getMyInvoices);
  return listOf(data).map(readInvoice);
};

/** Downloads the invoice PDF through the authorised API and saves it. */
export const downloadInvoicePdf = async (row: InvoiceRow): Promise<void> => {
  const { data } = await baseURL.get<Blob>(`${requestUrl.getMyInvoices}/${encodeURIComponent(row.id)}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(row.number ?? row.id).replace(/[^\w.-]+/g, "_")}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};

export interface HistoryRow {
  id: string;
  date: string | null;
  item: string | null;
  basis: string | null;
  quantity: number | null;
  amount: number | null;
  currency: string | null;
  status: string | null;
  method: string | null;
}

export interface HistoryPage {
  rows: HistoryRow[];
  page: number;
  perPage: number;
  /** Total rows when the API says; null when it doesn't. */
  total: number | null;
  /** Whether a next page exists (from the total, else from a full page). */
  hasMore: boolean;
}

export const readHistoryRow = (row: Raw, index: number): HistoryRow => ({
  id: text(pick(row, ["transaction_id", "transactionId", "order_id", "orderId", "payment_id", "paymentId", "id"])) ?? `payment-${index}`,
  date: text(pick(row, ["paid_at", "paidAt", "created_at", "createdAt", "payment_date", "paymentDate", "date"])),
  item: text(pick(row, ["package_name", "packageName", "package", "feature_name", "featureName", "description", "purchase_target", "purchaseTarget"])),
  basis: basisLabel(pick(row, ["billing_basis", "billingBasis"])),
  quantity: num(pick(row, ["quantity", "count"])),
  amount: num(pick(row, ["final_amount", "finalAmount", "amount", "total_amount", "totalAmount"])),
  currency: text(pick(row, ["currency", "currency_code", "currencyCode"])),
  status: text(pick(row, ["status", "payment_status", "paymentStatus"])),
  method: text(pick(row, ["payment_method", "paymentMethod", "mode"])),
});

/** The page's rows and total, wherever the API puts them (plain list, responseData, items / data / records). */
export const readHistoryPage = (data: unknown, page: number, perPage: number): HistoryPage => {
  const body = (data && typeof data === "object" && !Array.isArray(data) && (data as Raw).responseData !== undefined ? (data as Raw).responseData : data) as unknown;
  let list: Raw[] = [];
  let meta: Raw = {};
  if (Array.isArray(body)) list = body as Raw[];
  else if (body && typeof body === "object") {
    meta = body as Raw;
    const inner = pick(meta, ["items", "data", "records", "results", "history", "payments", "rows"]);
    if (Array.isArray(inner)) list = inner as Raw[];
  }
  const outer = data && typeof data === "object" && !Array.isArray(data) ? (data as Raw) : {};
  const total = num(pick(meta, ["total", "totalCount", "total_count", "totalRecords", "total_records", "count"]) ?? pick(outer, ["total", "totalCount", "total_count", "totalRecords"]));
  const rows = list.map((r, i) => readHistoryRow(r, page * perPage + i));
  return { rows, page, perPage, total, hasMore: total !== null ? (page + 1) * perPage < total : rows.length === perPage };
};

/** One page (0-based) of the user's payments. */
export const getPaymentHistory = async (page = 0, perPage = 10): Promise<HistoryPage> => {
  const { data } = await baseURL.get(requestUrl.getPaymentHistory, { params: { page, per_page: perPage } });
  return readHistoryPage(data, page, perPage);
};

export interface ProjectCredits {
  /** Extra projects bought (ProjectCreation credits), across every entitlement row. */
  purchased: number;
  /** Purchased credits already spent: projects beyond the free ones. */
  used: number;
  left: number;
  /** Free projects every account gets before credits are needed. */
  free: number;
  max: number;
  /** Whether another credit can be bought (feature exists and the cap isn't reached). */
  canBuy: boolean;
  feature: FeatureTy | null;
}

/**
 * Bought / used / left extra projects. Purchased comes from the entitlements; used is derived from
 * the project count (projects past the free limit spend a credit), the same way the dashboard
 * decides whether a new project needs one.
 * TODO: replace with a backend credits endpoint once it reports used/remaining per user.
 */
export const getProjectCredits = async (projectCount: number): Promise<ProjectCredits> => {
  const feature = await getFeatureByName(PROJECT_CREATION_FEATURE);
  const purchased = feature ? await getPurchasedCredits(feature.featureId) : 0;
  const used = Math.min(purchased, Math.max(0, projectCount - projectCreateLimit));
  return {
    purchased,
    used,
    left: purchased - used,
    free: projectCreateLimit,
    max: maxPurchasedProjects,
    canBuy: !!feature && purchased < maxPurchasedProjects,
    feature,
  };
};
